import React from "react";
import "../styles/service.css";
import Navbar from "../components/layout/Navbar";
import SeoHead from "../components/SeoHead";

const CONDITIONS = [
  {
    title: "Compressive Neuropathies",
    desc: "Treatment for carpal and cubital tunnel disorders affecting hand function.",
  },
  {
    title: "Endoscopic Tunnel Release",
    desc: "Minimally invasive release of carpal and cubital tunnel compression.",
  },
  {
    title: "Nerve Injury Repair",
    desc: "Microsurgical repair of damaged peripheral nerves to restore sensation.",
  },
  {
    title: "Nerve Grafting",
    desc: "Bridging nerve gaps with grafts to reconnect injured nerve pathways.",
  },
  {
    title: "Nerve Transfers",
    desc: "Redirecting healthy nerves to restore lost movement and function.",
  },
  {
    title: "Cubital Tunnel Syndrome",
    desc: "Relief for ulnar nerve compression causing tingling and hand weakness.",
  },
  {
    title: "Nerve Tumors",
    desc: "Diagnosis and careful surgical removal of benign and malignant nerve tumors.",
  },
  {
    title: "Reconstructive Nerve Surgery",
    desc: "Complex reconstruction to restore function after severe nerve injuries.",
  },
  {
    title: "Tendon Transfers & Joint Fusion",
    desc: "Restoring strength and stability when nerve recovery is not possible.",
  },
];

const GALLERY = [
  {
    src: "/images/peripheral&brachial1.jpeg",
    alt: "Peripheral & Brachial Surgery 1",
  },
  {
    src: "/images/peripheral&brachial2.jpeg",
    alt: "Peripheral & Brachial Surgery 2",
  },
  {
    src: "/images/peripheral&brachial3.jpeg",
    alt: "Peripheral & Brachial Surgery 3",
  },
];

export default function PeripheralSurgery() {
  return (
    <div className="hs-page">

      <SeoHead
        title="Peripheral Nerve Surgery in Hyderabad | Dr. R. Suneel"
        description="Peripheral nerve surgery including nerve repair, grafting, decompression and transfers to restore function and sensation. Consult Dr. R. Suneel."
        keywords="peripheral nerve surgery Hyderabad, peripheral nerve surgeon Hyderabad, nerve injury treatment Hyderabad, peripheral nerve specialist Hyderabad, nerve repair surgery Hyderabad, nerve graft surgery Hyderabad, nerve decompression surgery, nerve compression treatment Hyderabad, microsurgery for nerve injuries, neuroma treatment Hyderabad, Dr R Suneel peripheral nerve surgeon, Medicover peripheral nerve surgery Hyderabad"
        url="https://www.handandwristclinics.com/peripheral"
        image="https://www.handandwristclinics.com/static/media/peripheral_surgery.png"
      />

      {/* HERO + BREADCRUMB */}
      <header className="hs-hero">
        <div className="hs-hero-inner">
          <nav className="hs-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a>
            <span className="hs-bc-sep">/</span>
            <a href="/services">Services</a>
            <span className="hs-bc-sep">/</span>
            <span className="hs-bc-current">Peripheral nerve Surgery</span>
          </nav>
          <div className="hs-eyebrow">
            <span></span> Specialized Hand & Wrist Care
          </div>
          <h1 className="hs-title">
            Peripheral nerve hand<em>Surgery</em>
          </h1>
          <div className="hs-divider" />
          <p className="hs-hero-sub">
            Advanced surgical treatment to restore function, strength, and mobility
            to injured or impaired hands.
          </p>
        </div>
      </header>

      {/* INTRO */}
      <section className="simple-content">
  <div className="container">

    <h2>Peripheral Nerve Surgery</h2>
    <p>
      Peripheral nervous system involves 43 pairs of nerves which split from the central nervous system. 
      This wide network of nerves connects the brain and spinal cord to the rest of the body, enabling proper 
      movement and sensation. Nerve injuries can be distressing and require timely diagnosis and treatment 
      by a specialist to restore optimal functioning.
    </p>

    <h3>Causes And Symptoms Of Peripheral Nerve Injury</h3>
    <p>
      Causes may include compression, trauma, or tumors. Symptoms depend on severity. Mild cases may show 
      tingling or numbness, while severe cases may involve continuous burning pain, loss of sensation, 
      or reduced movement in the affected area.
    </p>

    <h3>Treatment</h3>
    <p>
      Before treatment, a detailed history, physical examination, and diagnostic tests are required. 
      Treatment depends on the severity of the condition and may involve surgical or non-surgical approaches.
    </p>

    <p>
      Peripheral nerve surgery helps restore function, reduce pain, and improve quality of life. 
      It may include nerve repair, grafting, or rerouting of healthy nerves to regain lost function.
    </p>

  </div>
</section>

      {/* CONDITIONS GRID */}
      <section className="hs-section hs-conditions">
        <div className="hs-container">
          <div className="hs-section-head">
            <div className="hs-section-tag">Conditions We Treat</div>
            <h2 className="hs-section-title">
              Comprehensive <em>hand & wrist</em> treatment
            </h2>
            <div className="hs-divider" />
            <p className="hs-section-sub">
              From traumatic injuries to congenital conditions, we offer surgical
              expertise across the full spectrum of wrist disorders.
            </p>
          </div>

          <div className="hs-grid">
            {CONDITIONS.map((c, i) => (
              <article className="hs-card" key={i}>
                <div className="hs-card-num">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="hs-card-title">{c.title}</h3>
                <p className="hs-card-desc">{c.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SMALL TEXT / CLOSING NOTE */}
      <section className="hs-section hs-note">
        <div className="hs-container hs-note-inner">
          <p className="hs-note-text">
            Every hand is unique — and so is every treatment plan. Our team
            combines microsurgical precision with years of specialist experience to
            deliver personalized care tailored to your recovery goals.
          </p>
        </div>
      </section>

      {/* IMAGE GALLERY */}
      <section className="hs-section hs-gallery-section">
        <div className="hs-container">
          <div className="hs-section-head hs-center">
            <div className="hs-section-tag">Case Gallery</div>
            <h2 className="hs-section-title">
              Results from our <em>Peripheral nerve surgery</em> cases
            </h2>
            <div className="hs-divider hs-center-divider" />
          </div>

          <div className="hs-gallery">
            {GALLERY.map((img, i) => (
              <figure className="hs-gallery-item" key={i}>
                <img src={img.src} alt={img.alt} loading="lazy" />
                <figcaption className="hs-gallery-cap">
                  Case {String(i + 1).padStart(2, "0")}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      
    </div>
  );
}